/**
 * `IconPickerField` - a lucide icon picker in a popover, shared by the Services and
 * Packages forms (web_admin :3212 + web_crm :3213). Same UX as the page builder's
 * `IconPicker`, but self-contained: it does not depend on `@wa/builder` or
 * `useBuilderEnv` (avoids a circular `@wa/ui` -> `@wa/builder` dependency). i18n
 * comes from `plans/i18n`.
 *
 * Storage format: kebab-case (e.g. `shopping-bag`) = the key form of lucide's dynamic
 * imports. Rendering reuses `ServiceIcon` (`lucide-react/dynamic` `DynamicIcon`,
 * Layers fallback). The search grid shows only `pageSize` icons at a time and an
 * IntersectionObserver loads more - avoids rendering 1900+ icons at once.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Sparkles, ImageOff } from "lucide-react";
import dynamicIconImports from "lucide-react/dynamicIconImports";
import { Button } from "../components/button";
import { Input } from "../components/input";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../components/popover";
import { cn } from "../lib/utils";
import { ServiceIcon } from "./ServiceIcon";
import { makeT } from "./i18n";
import type { Locale } from "./types";

const ICON_IMPORTS = dynamicIconImports as unknown as Record<string, unknown>;
const ALL_NAMES: string[] = Object.keys(ICON_IMPORTS).sort();

// Curated most common icons - shown first when the search is empty. Kebab-case.
const CURATED: string[] = [
  "server", "globe", "layers", "package", "rocket", "shield-check", "sparkles",
  "zap", "cloud", "database", "code", "settings", "star", "heart", "gift",
  "tag", "phone", "mail", "map-pin", "clock", "users", "user", "home",
  "search", "lock", "trending-up", "target", "lightbulb", "check", "award",
];

export function IconPickerField({
  value,
  onChange,
  locale,
}: {
  value: string;
  onChange: (next: string) => void;
  locale: Locale;
}) {
  const t = makeT(locale);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(96);

  useEffect(() => { if (!open) { setPageSize(96); setQuery(""); } }, [open]);

  // On open, scroll the search field into view and focus it.
  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => {
      searchRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      searchRef.current?.focus({ preventScroll: true });
    }, 50);
    return () => window.clearTimeout(id);
  }, [open]);

  // Search score: exact > prefix > token-prefix > include. Empty query -> curated
  // first, then the rest alphabetically. The token-prefix test has to run BEFORE the
  // substring test: any name whose word starts with the query also contains it, so
  // with the substring test first the word-prefix branch could never be reached and
  // "arrow-up" ranked the same as "cup-soda" for "up" - hence `iconSearchScore`.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const curatedExisting = CURATED.filter((n) => n in ICON_IMPORTS);
      const curatedSet = new Set(curatedExisting);
      const rest = ALL_NAMES.filter((n) => !curatedSet.has(n));
      return [...curatedExisting, ...rest];
    }
    const out: Array<{ name: string; score: number }> = [];
    for (const name of ALL_NAMES) {
      const score = iconSearchScore(name, q);
      if (score > 0) out.push({ name, score });
    }
    out.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
    return out.map((r) => r.name);
  }, [query]);

  const visible = results.slice(0, pageSize);

  // IntersectionObserver - sentinel visible -> +96 icons. The sentinel is held in
  // state, not a ref: the popover panel portals in one render after `open` flips, so
  // an effect keyed on `open` would still see an empty ref and never observe.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first && first.isIntersecting) {
        setPageSize((n) => Math.min(n + 96, results.length));
      }
    }, { rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, [sentinel, pageSize, results.length]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      {/* The panel spans the whole row (button + input + clear), as the old inline
          panel did, so it anchors to the row rather than to the small trigger. */}
      <PopoverAnchor asChild>
        <div className="flex items-center gap-2">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              title={t("icon.pick")}
              aria-label={t("icon.pick")}
              className="size-9 shrink-0 bg-muted/40 text-muted-foreground hover:text-foreground"
            >
              {value ? <ServiceIcon name={value} className="size-4" /> : <Sparkles className="size-4" />}
            </Button>
          </PopoverTrigger>
          <Input
            value={value}
            placeholder="server"
            onChange={(e) => onChange(e.target.value.trim())}
            className="flex-1"
          />
          {value && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange("")}
              className="shrink-0 px-2 text-muted-foreground hover:bg-transparent hover:text-foreground"
            >
              {t("icon.clear")}
            </Button>
          )}
        </div>
      </PopoverAnchor>

      <PopoverContent
        // The search field takes focus in the effect above; stop Radix from moving
        // focus to the first icon button first.
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex w-(--radix-popper-anchor-width) flex-col gap-2 p-2"
      >
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPageSize(96); }}
            placeholder={t("icon.search")}
            className="h-9 pl-8"
          />
        </div>
        <div className="flex items-center justify-between px-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          <span>{query ? `${results.length} ${t("icon.results")}` : t("icon.popular")}</span>
          <span className="tabular-nums">{ALL_NAMES.length} {t("icon.total")}</span>
        </div>
        <div
          className="icon-picker-scroll max-h-72 overflow-y-auto overscroll-contain"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <style>{`.icon-picker-scroll::-webkit-scrollbar{width:0;height:0;display:none}`}</style>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(2rem,1fr))] gap-0.5">
            {visible.length === 0 && (
              <div className="col-span-full flex flex-col items-center gap-1 py-6 text-center text-xs text-muted-foreground">
                <ImageOff className="size-4" />
                <span>{t("icon.noResults")}</span>
              </div>
            )}
            {visible.map((name) => (
              <Button
                key={name}
                variant="ghost"
                size="icon-sm"
                title={name}
                aria-label={name}
                aria-pressed={value === name}
                onClick={() => { onChange(name); setOpen(false); }}
                className={cn(
                  "border active:scale-100",
                  value === name ? "border-primary bg-primary/10 text-primary" : "border-transparent text-foreground",
                )}
              >
                <ServiceIcon name={name} className="size-4" />
              </Button>
            ))}
          </div>
          {/* Sentinel: when visible, load 96 more icons. */}
          {results.length > visible.length && (
            <div ref={setSentinel} className="flex h-8 items-center justify-center text-[10px] text-muted-foreground">
              {t("icon.loadMore")}… ({results.length - visible.length})
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * How well an icon name matches a lower-cased query: exact 100, name prefix 80, the
 * prefix of one of its dash-separated words 50, anywhere else 40, no match 0.
 */
export function iconSearchScore(name: string, q: string): number {
  if (name === q) return 100;
  if (name.startsWith(q)) return 80;
  if (name.split("-").some((tk) => tk.startsWith(q))) return 50;
  if (name.includes(q)) return 40;
  return 0;
}
